<template>
  <button
    v-if="reviewItem.pdfFile?.path"
    v-tooltip="'Full view'"
    class="h-7.5 w-7.5 p-1.5 rounded-lg border border-gray-200"
    @click.stop="goToFullView"
  >
    <PdfIcon class="h-4 w-4 text-red-500" />
  </button>

  <div v-else-if="isCuttingEdge && reviewItem.metadata?.doi && isMiraIntegrationEnabled">
    <FloatingMenu ref="floatingMenuRef">
      <template #activator="{ show }">
        <button class="p-1.5 rounded-lg border border-gray-200" @click.stop="[showPrice(show)]">
          <PdfIcon class="h-4 w-4 text-slate-500" />
        </button>
      </template>
      <template #content>
        <div class="min-w-[220px] rounded-xl bg-white flex flex-col gap-1">
          <button
            v-tooltip="orderButtonTooltip"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-slate-100 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            :disabled="isOrderButtonDisabled"
            @click.stop="showConfirmModal"
          >
            <i-material-symbols-shopping-cart class="h-6 w-6" />
            <span class="whitespace-nowrap">
              <template v-if="isPdfPriceLoading">Loading...</template>
              <template v-else-if="orderState.isAlreadyRequested">PDF Requested</template>
              <template v-else-if="orderState.canShowPrice"
                >Request PDF ({{ orderState.formattedPrice }})</template
              >
              <template v-else>Request PDF</template>
            </span>
          </button>
          <button
            class="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-slate-100 transition"
            @click.stop="handleUploadClick"
          >
            <i-material-symbols-picture-as-pdf class="h-6 w-6" />
            <span class="whitespace-nowrap">Upload PDF</span>
          </button>
        </div>
      </template>
    </FloatingMenu>

    <Modal ref="confirmModalRef" position="center">
      <template #header="{ hide }">
        <p class="text-primary text-lg font-semibold text-center flex gap-2 items-center">
          Confirm PDF Order
        </p>
        <button
          class="bg-light-neutral-500 rounded-full h-[30px] w-[30px] flex items-center justify-center"
          @click="hide"
        >
          <i-material-symbols-close class="size-4 stroke-2 text-mid-neutral-500" />
        </button>
      </template>

      <template #content>
        <div class="w-full space-y-4">
          <p class="text-gray-600">
            Are you sure you want to place an order for this PDF?
            <template v-if="orderState.canShowPrice">
              <br />
              <span class="font-semibold"> Price: {{ orderState.formattedPrice }} </span>
            </template>
          </p>
        </div>
      </template>

      <template #footer="{ hide }">
        <div class="flex justify-end items-center gap-2">
          <ActionButton color="neutral" @click="hide">Cancel</ActionButton>
          <ActionButton color="primary" @click="confirmOrder(hide)"> Confirm </ActionButton>
        </div>
      </template>
    </Modal>
  </div>

  <button
    v-else
    v-tooltip="'Drag and drop or click to upload a PDF'"
    class="p-1.5 rounded-lg border border-gray-200"
    @click.stop="emit('triggerUpload')"
  >
    <PdfIcon class="h-4 w-4 text-slate-500" />
  </button>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import PdfIcon from "@app/components/Icons/PdfIcon.vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import Modal from "@app/components/Global/Modal/Modal.vue";
import ActionButton from "@app/components/Global/ActionButton.vue";
import { DevToolboxKey } from "@app/injectionKeys";
import { RouteName } from "@app/router";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { useRoute, useRouter } from "vue-router";

const { isCuttingEdge } = injectStrict(DevToolboxKey);

const floatingMenuRef = ref<InstanceType<typeof FloatingMenu>>();
const confirmModalRef = ref<InstanceType<typeof Modal>>();

const props = defineProps<{
  reviewItem: ReviewItem;
  pdfPrice?: number | null;
  isPdfPriceLoading?: boolean;
  isCreateArticlePdfLoading?: boolean;
  isMiraIntegrationEnabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "triggerUpload"): void;
  (e: "fetchPdfPrice"): void;
  (e: "createArticlePdfOrder"): void;
}>();

const orderState = computed(() => {
  const price = props.pdfPrice;
  const isRequested = price !== null && price !== undefined && price < 0;
  const isLoading = !!props.isPdfPriceLoading;

  return {
    isAlreadyRequested: isRequested,
    isLoading,
    isButtonDisabled: isRequested || isLoading,
    canShowPrice: !isRequested && price !== null && price !== undefined,
    formattedPrice:
      price !== null && price !== undefined && price >= 0 ? `$${price.toFixed(2)}` : "",
  };
});

const isOrderButtonDisabled = computed(() => orderState.value.isButtonDisabled);

const orderButtonTooltip = computed(() => {
  if (orderState.value.isAlreadyRequested) {
    return "PDF order requested. Awaiting delivery from Mira.";
  }
  if (orderState.value.isLoading) {
    return "Loading price...";
  }
  return "Place an order with Mira";
});

const router = useRouter();
const route = useRoute();

function showConfirmModal() {
  floatingMenuRef.value?.hide();
  confirmModalRef.value?.show();
}

function handleUploadClick() {
  floatingMenuRef.value?.hide();
  emit("triggerUpload");
}

function showPrice(show: () => void) {
  emit("fetchPdfPrice");
  show();
}

function confirmOrder(hide: () => void) {
  emit("createArticlePdfOrder");
  hide();
}

function goToFullView() {
  let activeStep = "screening";
  if (route.name === RouteName.REVIEW_EXECUTE_DATA_EXTRACTION) activeStep = "extraction";
  else if (route.name === RouteName.REVIEW_EXECUTE_APPRAISAL) activeStep = "appraisal";

  router.push({
    name: "Review - Review Item",
    params: { reviewItemId: props.reviewItem.id },
    query: { step: activeStep },
  });
}
</script>
